// URL parameter management for video persistence and sharing

/**
 * Encode video data into URL parameters
 * @param {string} videoUrl - The full video URL with FPS info
 * @param {string} filename - The filename for display
 * @param {number} fps - The video FPS
 */
export const encodeVideoToURL = (videoUrl, filename, fps) => {
  try {
    const videoData = {
      url: videoUrl,
      filename: filename || '',
      fps: fps || null
    };
    
    // Encode to base64 to handle special characters safely
    const encoded = btoa(JSON.stringify(videoData));
    
    // Update URL without page reload
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('video', encoded);
    
    window.history.replaceState(null, '', newUrl);
    
    return newUrl.href;
  } catch (error) {
    console.warn('Failed to encode video to URL:', error);
    return window.location.href;
  }
};

/**
 * Decode video data from URL parameters
 * @returns {Object|null} Video data or null if not found
 */
export const decodeVideoFromURL = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const encoded = urlParams.get('video');
    
    if (!encoded) return null;
    
    const decoded = atob(encoded);
    const videoData = JSON.parse(decoded);
    
    return {
      url: videoData.url || '',
      filename: videoData.filename || '',
      fps: videoData.fps || null
    };
  } catch (error) {
    console.warn('Failed to decode video from URL:', error);
    return null;
  }
};

/**
 * Remove video parameter from URL
 */
export const clearVideoFromURL = () => {
  try {
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('video');
    
    window.history.replaceState(null, '', newUrl);
  } catch (error) {
    console.warn('Failed to clear video from URL:', error);
  }
};

/**
 * Check if current URL has video parameter
 * @returns {boolean}
 */
export const hasVideoInURL = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has('video');
};

/**
 * Generate a shareable URL for the current video
 * @param {string} videoUrl - The video URL
 * @param {string} filename - The filename
 * @param {number} fps - The FPS
 * @returns {string} Complete shareable URL
 */
export const generateShareableURL = (videoUrl, filename, fps) => {
  const baseUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  const videoData = {
    url: videoUrl,
    filename: filename || '',
    fps: fps || null
  };
  
  const encoded = btoa(JSON.stringify(videoData));
  return `${baseUrl}?video=${encodeURIComponent(encoded)}`;
};

/**
 * Listen for browser back/forward navigation
 * @param {Function} callback - Function to call when video URL changes
 */
export const setupURLChangeListener = (callback) => {
  const handlePopState = () => {
    const videoData = decodeVideoFromURL();
    callback(videoData);
  };
  
  window.addEventListener('popstate', handlePopState);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('popstate', handlePopState);
  };
};

/**
 * Validate and clean video URL parameter
 * @param {string} videoUrl - Raw video URL
 * @returns {Object} Validation result with cleaned URL
 */
export const validateVideoURL = (videoUrl) => {
  if (!videoUrl || typeof videoUrl !== 'string') {
    return { isValid: false, error: 'No video URL provided' };
  }
  
  const trimmed = videoUrl.trim();
  
  // Check for the expected VIDEO:url,FPS:value format
  if (!trimmed.includes('VIDEO:') || !trimmed.includes('FPS:')) {
    return { 
      isValid: false, 
      error: 'Invalid format. Expected: VIDEO:url,FPS:value' 
    };
  }
  
  return { 
    isValid: true, 
    cleanedUrl: trimmed 
  };
};
